import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import { inputFieldStyles, textFieldStyles } from '@utils/styles';
import { RootState } from '@stores/store';
import useSubmitFeedback from '@hooks/api/useFeedback';
import { TOAST_TYPES } from '@constants/constants';
import { setToastMessage } from '@stores/App/slice';
import CustomButton from '../../../components/CustomButton/CustomButton';
import FeedbackConfirmModal from '../components/Feedback/FeedbackConfirmModal';
import {
  feedbackOptions,
  MAX_TOTAL_SIZE_MB,
  MAX_IMAGE_COUNT,
  MAX_VIDEO_COUNT,
  MAX_PDF_COUNT,
  MAX_FILE_COUNT,
  MAX_FEEDBACK_LENGTH,
} from '../components/Feedback/FeedbackConstants';

type Attachment = {
  id: string;
  file: File;
};

const FeedbackDashboard = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const dispatch = useDispatch();

  const userData = useSelector((state: RootState) => state?.UserData);
  const { sendFeedback, isLoading, isError, isSuccess } = useSubmitFeedback();

  const fileInputRef = useRef<HTMLInputElement>(null);

  const [selected, setSelected] = useState(null);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [open, setOpen] = useState(false);
  const [feedback, setFeedback] = useState('');

  useEffect(() => {
    if (isSuccess) {
      setOpen(true);
    }
    if (isError) {
      dispatch(
        setToastMessage({
          type: TOAST_TYPES.ERROR,
          message: `Feedback not submitted`,
        }),
      );
    }
  }, [isSuccess, isError]);

  const handleSendClick = () => {
    sendFeedback({
      type: selected,
      text: feedback,
      attachments: attachments.map((att) => {
        return att.file;
      }),
    });
  };

  const handleCloseModal = () => {
    setOpen(false);
    setFeedback('');
    setAttachments([]);
    setSelected(null);
  };

  const handleChange = (option: any) => {
    setSelected((prev) => (prev === option ? null : option));
  };

  const handleAttachmentClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      const currentAttachments = [...attachments];

      const imageCount = currentAttachments.filter((att) =>
        att.file.type.startsWith('image'),
      ).length;
      const videoCount = currentAttachments.filter((att) =>
        att.file.type.startsWith('video'),
      ).length;
      const pdfCount = currentAttachments.filter(
        (att) => att.file.type === 'application/pdf',
      ).length;

      const currentTotalSize = currentAttachments.reduce((acc, att) => acc + att.file.size, 0);
      const MAX_TOTAL_SIZE_BYTES = MAX_TOTAL_SIZE_MB * 1024 * 1024;

      const filteredFiles: Attachment[] = [];

      Array.from(files).forEach((file) => {
        const isValidType = [
          'image/jpeg',
          'image/jpg',
          'image/png',
          'application/pdf',
          'video/mp4',
        ].includes(file.type);
        const projectedTotalSize =
          currentTotalSize + filteredFiles.reduce((acc, f) => acc + f.file.size, 0) + file.size;
        if (projectedTotalSize > MAX_TOTAL_SIZE_BYTES) {
          dispatch(
            setToastMessage({
              type: TOAST_TYPES.ERROR,
              message: `Total attachments cannot exceed ${MAX_TOTAL_SIZE_MB}MB.`,
            }),
          );
          return;
        }
        if (!isValidType) {
          dispatch(
            setToastMessage({
              type: TOAST_TYPES.ERROR,
              message: `File "${file.name}" is not a supported format.`,
            }),
          );
          return;
        }
        if (file.type.startsWith('image')) {
          if (
            imageCount + filteredFiles.filter((f) => f.file.type.startsWith('image')).length >=
            MAX_IMAGE_COUNT
          ) {
            dispatch(
              setToastMessage({
                type: TOAST_TYPES.ERROR,
                message: `You can only upload up to ${MAX_IMAGE_COUNT} image files.`,
              }),
            );
            return;
          }
        }
        if (file.type === 'video/mp4') {
          if (
            videoCount + filteredFiles.filter((f) => f.file.type === 'video/mp4').length >=
            MAX_VIDEO_COUNT
          ) {
            dispatch(
              setToastMessage({
                type: TOAST_TYPES.ERROR,
                message: `You can only upload up to ${MAX_VIDEO_COUNT} video files.`,
              }),
            );
            return;
          }
        }
        if (file.type === 'application/pdf') {
          if (
            pdfCount + filteredFiles.filter((f) => f.file.type === 'application/pdf').length >=
            MAX_PDF_COUNT
          ) {
            dispatch(
              setToastMessage({
                type: TOAST_TYPES.ERROR,
                message: `You can only upload up to ${MAX_PDF_COUNT} PDF files.`,
              }),
            );
            return;
          }
        }
        if (currentAttachments.length + filteredFiles.length >= MAX_FILE_COUNT) {
          dispatch(
            setToastMessage({
              type: TOAST_TYPES.ERROR,
              message: `You can only attach up to ${MAX_FILE_COUNT} files total.`,
            }),
          );
          return;
        }
        filteredFiles.push({
          id: `${file.name}-${Date.now()}`,
          file,
        });
      });

      setAttachments((prev) => [...prev, ...filteredFiles]);
    }
  };

  const handleDelete = (id: string) => {
    setAttachments((prev) => prev.filter((att) => att.id !== id));
  };

  const handleFeedbackTextChange = (e: any) => {
    if (e.target.value.length <= MAX_FEEDBACK_LENGTH) {
      setFeedback(e.target.value);
    } else {
      dispatch(
        setToastMessage({
          type: TOAST_TYPES.ERROR,
          message: `You can only enter up to ${MAX_FEEDBACK_LENGTH} characters.`,
        }),
      );
    }
  };

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        overflow: 'scroll',
        background: '#F5F5F5',
        padding: isMobile ? '1rem 2rem 4rem 2rem' : '1rem 2rem 1rem 2rem',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          marginTop: '0.5rem',
          marginBottom: '2rem',
        }}
      >
        <Typography
          sx={{
            fontFamily: 'Manrope, sans-serif',
            fontSize: '2rem',
            fontWeight: 600,
            lineHeight: '1.5rem',
          }}
        >
          Feedback
        </Typography>
      </Box>
      <Box
        sx={{
          width: '100%',
          backgroundColor: '#FFFFFF',
          padding: '1.2rem 1.8rem',
          borderRadius: '1rem',
        }}
      >
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: '1rem',
            lineHeight: '1.375rem',
            color: '#162936',
            marginBottom: '1rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          Your Details
        </Typography>
        <Box
          sx={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '1rem',
          }}
        >
          <TextField
            sx={{
              ...textFieldStyles,
              width: '100%',
            }}
            value={userData?.user?.email}
            label="Email"
            placeholder="Email"
            disabled={true}
          />
          <TextField
            sx={{
              ...textFieldStyles,
              width: '100%',
            }}
            value={userData?.user?.company}
            label="Company Name"
            placeholder="Company Name"
            disabled={true}
          />
        </Box>
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: '1rem',
            lineHeight: '1.375rem',
            color: '#162936',
            marginBottom: '1rem',
            marginTop: '3rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          What would you like to share with us?
        </Typography>
        <Box
          sx={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '1rem',
          }}
        >
          {feedbackOptions.map(({ label, value }) => (
            <FormControlLabel
              key={value}
              sx={{
                width: '100%',
                border: '1px solid #7FBCAD',
                borderRadius: '0.5rem',
                padding: '0.5rem',
                marginLeft: '0px',
                marginRight: '0px',
                '& .MuiFormControlLabel-label': {
                  fontFamily: 'Manrope, sans-serif',
                  fontSize: '0.875rem',
                  fontWeight: 700,
                  color: '#162936',
                },
              }}
              disabled={isLoading}
              label={label}
              control={
                <Checkbox
                  checked={selected === value}
                  onChange={() => handleChange(value)}
                  icon={<RadioButtonUncheckedIcon />}
                  checkedIcon={<CheckCircleIcon />}
                />
              }
            />
          ))}
        </Box>
        {selected && (
          <Box
            sx={{
              marginTop: '2rem',
              display: 'flex',
              flexDirection: 'column',
              gap: '1rem',
            }}
          >
            <TextField
              multiline
              minRows={12}
              sx={{
                ...textFieldStyles,
                width: '100%',
                '& .MuiInputBase-root': {
                  alignItems: 'flex-start',
                },
                '& .MuiInputBase-input': {
                  ...inputFieldStyles,
                  maxHeight: '15rem',
                  resize: 'none',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  overflowY: 'auto !important',
                  '&::-webkit-scrollbar': {
                    width: '0.25rem',
                  },
                  '&::-webkit-scrollbar-track': {
                    backgroundColor: '#E0E0E0',
                    borderRadius: '4px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: '#2F736E',
                    borderRadius: '4px',
                  },
                },
              }}
              disabled={isLoading}
              value={feedback}
              onChange={handleFeedbackTextChange}
              label="Enter Feedback Here"
              inputProps={{ maxLength: MAX_FEEDBACK_LENGTH + 1 }}
              placeholder="Enter Feedback Here"
            />
            {attachments.length > 0 && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  flexWrap: 'wrap',
                  gap: '0.5rem',
                }}
              >
                {attachments.map((attachment) => (
                  <Box
                    key={attachment.id}
                    sx={{
                      backgroundColor: '#F0F0F1',
                      padding: '1rem 0.5rem',
                      borderRadius: '0.5rem',
                      display: 'flex',
                      alignItems: 'center',
                      width: isMobile ? '60%' : '40%',
                      height: '3rem',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        width: isMobile ? '75%' : '85%',
                      }}
                    >
                      <InsertDriveFileOutlinedIcon sx={{ color: '#2F736E' }} />
                      <Typography
                        sx={{
                          color: '#6C6C6C',
                          fontWeight: 700,
                          fontSize: '0.75rem',
                          overflowX: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {attachment.file.name}
                      </Typography>
                    </Box>
                    <DeleteOutlinedIcon
                      sx={{ color: '#2F736E', cursor: 'pointer' }}
                      onClick={() => handleDelete(attachment.id)}
                    />
                  </Box>
                ))}
              </Box>
            )}
            <Box
              component="button"
              sx={{
                all: 'unset',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                color: attachments.length === 5 ? '#6C6C6C' : '#51B8B0',
                cursor: attachments.length === 5 ? 'default' : 'pointer',
                width: 'fit-content',
              }}
              disabled={attachments.length === 5}
              onClick={handleAttachmentClick}
            >
              <AttachFileIcon />
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: '0.875rem',
                }}
              >
                Attachment
              </Typography>
              <input
                type="file"
                accept=".jpeg,.jpg,.png,.pdf,.mp4"
                multiple
                hidden
                ref={fileInputRef}
                onChange={handleFileChange}
              />
            </Box>
            {isLoading ? (
              <Box
                sx={{
                  width: '9.25rem',
                  height: '2.563rem',
                  backgroundColor: '#2F736E',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '2rem',
                }}
              >
                <CircularProgress sx={{ color: '#F6F8F9' }} size={20} />
              </Box>
            ) : (
              <CustomButton
                width="9.25rem"
                color={'#F6F8F9'}
                backgroundColor="#2F736E"
                name={'Send Feedback'}
                onClick={handleSendClick}
                disabled={!feedback.trim()}
              />
            )}
            <FeedbackConfirmModal open={open} close={handleCloseModal} />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default FeedbackDashboard;
